/**
 * Starter block lists for new funding appeal email templates.
 */

import type { EmailBlock } from "./fundingEmailTemplateBlocks";
import { validateEmailBlocks } from "./fundingEmailTemplateBlocks";

export type FundingEmailTemplateDef = {
  id: string;
  label: string;
  description: string;
  /** Suggested template name when creating from this starter. */
  defaultName: string;
  blocks: EmailBlock[];
};

export const FUNDING_EMAIL_TEMPLATE_IDS = ["blank", "initial-outreach", "follow-up", "thank-you"] as const;
export type FundingEmailTemplateId = (typeof FUNDING_EMAIL_TEMPLATE_IDS)[number];

export const FUNDING_EMAIL_TEMPLATES: FundingEmailTemplateDef[] = [
  {
    id: "blank",
    label: "Blank",
    description: "Greeting, empty paragraph, appeal button, and sign-off — fill in your own wording.",
    defaultName: "New template",
    blocks: [
      { type: "greeting", text: "Hi {{contact_name}}," },
      { type: "paragraph", text: "<p>Write your message here.</p>" },
      { type: "button", label: "View our appeal" },
      { type: "social_links" },
      { type: "signoff", lines: ["Best regards,", "{{club_name}}"] },
    ],
  },
  {
    id: "initial-outreach",
    label: "Initial outreach",
    description: "Introduce {{appeal_name}} and ask for support with a link to the appeal page.",
    defaultName: "Initial outreach",
    blocks: [
      { type: "greeting", text: "Hi {{contact_name}}," },
      {
        type: "paragraph",
        text: "<p>I'm writing from {{club_name}} about our fundraising appeal, <strong>{{appeal_name}}</strong>. We're raising {{appeal_target}} and would be grateful for your support.</p>",
      },
      {
        type: "paragraph",
        text: "<p>If you have a moment, please take a look at the appeal page — any contribution helps us move this forward.</p>",
      },
      {
        type: "donation_tiers",
        heading: "Suggested support levels",
        body: "Choose a level that works for you.",
        buttonLabel: "Get in touch",
      },
      { type: "button", label: "View our appeal" },
      { type: "social_links" },
      { type: "signoff", lines: ["Thank you,", "{{club_name}}"] },
    ],
  },
  {
    id: "follow-up",
    label: "Follow-up",
    description: "A short nudge for funders who have not replied yet.",
    defaultName: "Follow-up",
    blocks: [
      { type: "greeting", text: "Hi {{contact_name}}," },
      {
        type: "paragraph",
        text: "<p>I wanted to follow up on my earlier note about <strong>{{appeal_name}}</strong> at {{club_name}}.</p>",
      },
      {
        type: "paragraph",
        text: "<p>We're still working toward {{appeal_target}}. If you're able to help, or would like more detail, I'd appreciate hearing from you.</p>",
      },
      {
        type: "donation_tiers",
        heading: "Ways to support us",
        buttonLabel: "Contact us",
      },
      { type: "button", label: "View our appeal" },
      { type: "social_links" },
      { type: "signoff", lines: ["Best regards,", "{{club_name}}"] },
    ],
  },
  {
    id: "thank-you",
    label: "Thank-you",
    description: "Thank a funder after they have supported the appeal.",
    defaultName: "Thank-you",
    blocks: [
      { type: "greeting", text: "Hi {{contact_name}}," },
      {
        type: "paragraph",
        text: "<p>Thank you so much for supporting <strong>{{appeal_name}}</strong>. Your help means a great deal to everyone at {{club_name}}.</p>",
      },
      {
        type: "paragraph",
        text: "<p>We'll keep you posted as we put the funds to work. Please don't hesitate to reply if you have any questions.</p>",
      },
      { type: "social_links" },
      { type: "signoff", lines: ["With thanks,", "{{club_name}}"] },
    ],
  },
];

export const DEFAULT_FUNDING_EMAIL_TEMPLATE_STARTER_ID: FundingEmailTemplateId = "initial-outreach";

/** Resolve a starter id from the new-template URL (defaults to initial outreach). */
export function resolveFundingEmailTemplateStarterId(
  starter: string | null | undefined,
): FundingEmailTemplateId {
  if (starter && FUNDING_EMAIL_TEMPLATES.some((t) => t.id === starter)) {
    return starter as FundingEmailTemplateId;
  }
  return DEFAULT_FUNDING_EMAIL_TEMPLATE_STARTER_ID;
}

export function getFundingEmailTemplate(id: FundingEmailTemplateId): FundingEmailTemplateDef {
  const found = FUNDING_EMAIL_TEMPLATES.find((t) => t.id === id);
  if (!found) throw new Error(`Unknown funding email template starter: ${id}`);
  return found;
}

/** Validate every starter block list (call in tests and on registry changes). */
export function assertFundingEmailStartersValid(): void {
  for (const def of FUNDING_EMAIL_TEMPLATES) {
    validateEmailBlocks(def.blocks);
  }
}

assertFundingEmailStartersValid();
